import type { CopilotContextRef } from '@fos/shared/contracts/copilot';
import { useSyncExternalStore } from 'react';
import { copilotChat } from './chat-store';

/**
 * Stan panelu „Zapytaj AI” (jeden na aplikację): otwierają go ⌘K → „Zapytaj AI…”, przycisk w Dziś i widoki
 * z kontekstem (klient, projekt, wątek, zadanie, spotkanie, szkic). Panel renderuje `<AskAiHost />` (montowany raz,
 * w szkielecie aplikacji); bez hosta `askAi.open()` nic nie pokazuje.
 */
export interface AskAiState {
  open: boolean;
  /** kontekst bieżącego widoku (warstwa CURRENT Context Buildera) */
  contextRef: CopilotContextRef | null;
  /** nazwa kontekstu do nagłówka („REA Nieruchomości”) */
  contextLabel: string | null;
  /** tekst wstawiony do pola (np. „Zapamiętaj, że …”) — bez wysyłania */
  draft: string | null;
  /** zmienia się przy każdym otwarciu (panel ustawia fokus i tekst) */
  openedAt: number;
  /**
   * otwarte z mikrofonu dolnego menu (10.10.2026): panel od razu dyktuje do pola i wysyła po zakończeniu mówienia;
   * pod polem skróty „Wpisz ręcznie: czas · zadanie · notatka”
   */
  voice: boolean;
}

export interface AskAiOpenOptions {
  contextRef?: CopilotContextRef | null;
  contextLabel?: string | null;
  draft?: string | null;
  /** wejście głosem (mikrofon dolnego menu) */
  voice?: boolean;
}

let state: AskAiState = { open: false, contextRef: null, contextLabel: null, draft: null, openedAt: 0, voice: false };
const listeners = new Set<() => void>();

function set(next: AskAiState): void {
  state = next;
  for (const l of listeners) l();
}

export const askAi = {
  open(opts: AskAiOpenOptions = {}): void {
    set({
      open: true,
      contextRef: opts.contextRef ?? null,
      contextLabel: opts.contextLabel ?? null,
      draft: opts.draft ?? null,
      openedAt: Date.now(),
      voice: opts.voice ?? false,
    });
  },
  close(): void {
    set({ ...state, open: false });
  },
  getSnapshot(): AskAiState {
    return state;
  },
  subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  /** otwarty → zamknij, zamknięty → otwórz (bąbel AI, ⌘J / Ctrl+J; Fala 10) */
  toggle(): void {
    if (state.open) askAi.close();
    else askAi.open();
  },
  /** testy (także rozmowa w sesji karty) */
  reset(): void {
    set({ open: false, contextRef: null, contextLabel: null, draft: null, openedAt: 0, voice: false });
    copilotChat.reset();
  },
};

export function useAskAiState(): AskAiState {
  return useSyncExternalStore(askAi.subscribe, askAi.getSnapshot, askAi.getSnapshot);
}
